import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";

const isDev = process.env.NODE_ENV !== "production";

const profiles = {
  cheapcabgrab: {
    key: "cheapcabgrab",
    label: "CheapCabGrab",
    from: "support@cheapcabgrab.com",
    signatureBlock:
      "Kind regards,\n" +
      "CheapCabGrab Support\n" +
      "📞 Call Us: 01628 665040\n" +
      "💬 WhatsApp Us: https://wa.me/447593308428\n" +
      "✉️ Email Support: support@cheapcabgrab.com\n" +
      "🌐 Cheap Cab Grab: https://www.cheapcabgrab.com\n" +
      "🚐 MinibusGrab: https://www.minibusgrab.com\n" +
      "🚌 CheapCoachGrab: https://www.cheapcoachgrab.com\n" +
      "✈️ Airport Journey Planner: https://www.airportjourneyplanner.com",
    websiteUrl: "https://www.cheapcabgrab.com",
    accent: "#1d4ed8",
    surface: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
  },
  ajp: {
    key: "ajp",
    label: "Airport Journey Planner",
    from: "support@airportjourneyplanner.com",
    signatureBlock:
      "Kind regards,\n" +
      "Airport Journey Planner Support\n" +
      "📞 Call Us: 01628 665040\n" +
      "💬 WhatsApp Us: https://wa.me/447593308428\n" +
      "✉️ Email Support: support@airportjourneyplanner.com\n" +
      "✈️ Airport Journey Planner: https://www.airportjourneyplanner.com\n" +
      "🌐 Cheap Cab Grab: https://www.cheapcabgrab.com",
    websiteUrl: "https://www.airportjourneyplanner.com",
    accent: "#0f766e",
    surface: "linear-gradient(135deg, #052e2b 0%, #0f766e 100%)",
  },
};

const initialForm = {
  profile: "cheapcabgrab",
  to: "",
  cc: "",
  bcc: "",
  recipientName: "",
  subject: "",
  title: "",
  signatureBlock: profiles.cheapcabgrab.signatureBlock,
  body: "",
  attachInvoice: false,
  invoice: {
    brand: "cheapcabgrab",
    invoiceNumber: "",
    enquiryReference: "",
    customerName: "",
    customerAddress: "",
    customerEmail: "",
    description: "Private hire transport service",
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    journeyDate: "",
    amount: "",
    stripePaymentUrl: "",
    paymentNotes: "",
  },
};

function bodyToParagraphs(body) {
  return String(body || "")
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);
}

function signatureLink(line) {
  const match = String(line || "").trim().match(/^([^:]+):\s*(.+)$/);
  if (!match) return null;
  const label = match[1].trim();
  const value = match[2].trim();
  if (/^https?:\/\//i.test(value)) return { label, href: value };
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(value)) {
    return { label, href: `mailto:${value}` };
  }
  if (/^\+?[0-9\s()\-]+$/.test(value)) {
    return { label, href: `tel:${value.replace(/[^\d+]/g, "")}` };
  }
  if (/^www\./i.test(value)) return { label, href: `https://${value}` };
  return null;
}

export default function DevAdhocEmail() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [previewingInvoice, setPreviewingInvoice] = useState(false);
  const [invoiceMeta, setInvoiceMeta] = useState(null);
  const profile = profiles[form.profile] || profiles.cheapcabgrab;

  const previewParagraphs = useMemo(() => bodyToParagraphs(form.body), [form.body]);

  useEffect(() => {
    api.get("/api/dev/adhoc-email/meta")
      .then((response) => setInvoiceMeta(response.data?.invoice || null))
      .catch(() => setInvoiceMeta(null));
  }, []);

  if (!isDev) {
    return (
      <div style={{ minHeight: "70vh", display: "grid", placeItems: "center", padding: "32px" }}>
        <div style={{ maxWidth: "720px", background: "#fff", borderRadius: "24px", padding: "32px", boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)" }}>
          <h1 style={{ margin: "0 0 12px", fontSize: "32px" }}>Not available in production</h1>
          <p style={{ margin: 0, color: "#475467", lineHeight: 1.7 }}>
            This internal email composer is intended for local development only.
          </p>
        </div>
      </div>
    );
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleProfileChange(value) {
    const nextProfile = profiles[value] || profiles.cheapcabgrab;
    setForm((prev) => ({
      ...prev,
      profile: nextProfile.key,
      signatureBlock: nextProfile.signatureBlock,
    }));
  }

  function updateInvoiceField(key, value) {
    setForm((prev) => ({ ...prev, invoice: { ...prev.invoice, [key]: value } }));
  }

  function suggestInvoiceNumber(reference) {
    const normalized = String(reference || "").trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "");
    return normalized ? `CCG-${normalized}-01` : "";
  }

  async function previewInvoice() {
    setPreviewingInvoice(true);
    setError("");
    try {
      const invoice = { ...form.invoice, customerEmail: form.invoice.customerEmail || form.to.split(",")[0].trim() };
      const response = await api.post("/api/dev/adhoc-email/invoice-preview", { invoice }, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await err.response.data.text());
          setError(payload.error || "Failed to generate invoice preview");
        } catch (_) {
          setError("Failed to generate invoice preview");
        }
      } else {
        setError(err?.response?.data?.error || err?.message || "Failed to generate invoice preview");
      }
    } finally {
      setPreviewingInvoice(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSending(true);
    setError("");
    setSuccess(null);

    try {
      const response = await api.post("/api/dev/adhoc-email/send", form);
      setSuccess(response.data || { success: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Helmet>
        <title>Dev Adhoc Email Composer</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top left, rgba(29, 78, 216, 0.14), transparent 30%), linear-gradient(180deg, #eef4ff 0%, #f8fafc 45%, #edf6f3 100%)",
          padding: "28px 18px 56px",
        }}
      >
        <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
          <div style={{ marginBottom: "22px" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", padding: "8px 14px", borderRadius: "999px", background: "#ffffffcc", border: "1px solid rgba(148, 163, 184, 0.28)", color: "#334155", fontSize: "13px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Internal Tool
            </div>
            <h1 style={{ margin: "18px 0 10px", fontSize: "40px", lineHeight: 1.05, color: "#0f172a" }}>Ad hoc email composer</h1>
            <p style={{ margin: 0, maxWidth: "760px", color: "#475467", fontSize: "17px", lineHeight: 1.7 }}>
              Dev-only composer for sending formal outbound messages from either brand using the shared email template.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(360px, 0.9fr)", gap: "22px" }}>
            <form onSubmit={handleSubmit} style={{ background: "#ffffff", borderRadius: "28px", padding: "26px", boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)", border: "1px solid rgba(226, 232, 240, 0.9)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", marginBottom: "18px" }}>
                <Field label="Send from">
                  <select value={form.profile} onChange={(e) => handleProfileChange(e.target.value)} style={inputStyle}>
                    <option value="cheapcabgrab">CheapCabGrab</option>
                    <option value="ajp">Airport Journey Planner</option>
                  </select>
                </Field>
                <Field label="From address">
                  <input value={profile.from} readOnly style={{ ...inputStyle, background: "#f8fafc", color: "#475467" }} />
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", marginBottom: "18px" }}>
                <Field label="To">
                  <input value={form.to} onChange={(e) => updateField("to", e.target.value)} placeholder="customer@example.com" style={inputStyle} />
                </Field>
                <Field label="Recipient name">
                  <input value={form.recipientName} onChange={(e) => updateField("recipientName", e.target.value)} placeholder="Optional" style={inputStyle} />
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", marginBottom: "18px" }}>
                <Field label="CC">
                  <input value={form.cc} onChange={(e) => updateField("cc", e.target.value)} placeholder="Optional" style={inputStyle} />
                </Field>
                <Field label="BCC">
                  <input value={form.bcc} onChange={(e) => updateField("bcc", e.target.value)} placeholder="Optional" style={inputStyle} />
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", marginBottom: "18px" }}>
                <Field label="Subject">
                  <input value={form.subject} onChange={(e) => updateField("subject", e.target.value)} placeholder="Email subject" style={inputStyle} />
                </Field>
                <Field label="Title">
                  <input value={form.title} onChange={(e) => updateField("title", e.target.value)} placeholder="Optional heading inside the email" style={inputStyle} />
                </Field>
              </div>

              <div
                style={{
                  marginBottom: "18px",
                  border: "1px solid #dbe4f0",
                  borderRadius: "20px",
                  padding: "16px",
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#475467", marginBottom: "12px" }}>
                  Message
                </div>
                <Field label="Body">
                  <textarea
                    value={form.body}
                    onChange={(e) => updateField("body", e.target.value)}
                    placeholder={"Write the message here.\n\nUse a blank line between paragraphs."}
                    style={{ ...inputStyle, minHeight: "240px", resize: "vertical", marginBottom: "16px", background: "#fff" }}
                  />
                </Field>

                <Field label="Signature">
                  <textarea
                    value={form.signatureBlock}
                    onChange={(e) => updateField("signatureBlock", e.target.value)}
                    placeholder="Editable closing signature"
                    style={{ ...inputStyle, minHeight: "150px", resize: "vertical", background: "#fff" }}
                  />
                </Field>
              </div>

              <div style={{ marginBottom: "18px", border: "1px solid #bfdbfe", borderRadius: "20px", padding: "16px", background: "#eff6ff" }}>
                <label style={{ display: "flex", gap: "10px", alignItems: "center", fontWeight: 800, color: "#0f172a", marginBottom: form.attachInvoice ? "16px" : 0 }}>
                  <input
                    type="checkbox"
                    checked={form.attachInvoice}
                    onChange={(e) => setForm((prev) => ({ ...prev, attachInvoice: e.target.checked }))}
                  />
                  Attach a CheapCabGrab invoice PDF
                </label>

                {form.attachInvoice ? (
                  <div style={{ display: "grid", gap: "16px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                      <Field label="Invoice branding">
                        <select value={form.invoice.brand} onChange={(e) => updateInvoiceField("brand", e.target.value)} style={inputStyle}>
                          <option value="cheapcabgrab">CheapCabGrab</option>
                          <option value="minibusgrab">MinibusGrab</option>
                          <option value="cheapcoachgrab">CheapCoachGrab</option>
                          <option value="ajp">Airport Journey Planner</option>
                        </select>
                      </Field>
                      <Field label="Enquiry reference">
                        <input
                          value={form.invoice.enquiryReference}
                          onChange={(e) => {
                            const enquiryReference = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              invoice: {
                                ...prev.invoice,
                                enquiryReference,
                                invoiceNumber: prev.invoice.invoiceNumber || suggestInvoiceNumber(enquiryReference),
                              },
                            }));
                          }}
                          placeholder="ENQMTYTHD15"
                          style={inputStyle}
                        />
                      </Field>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                      <Field label="Invoice number">
                        <input value={form.invoice.invoiceNumber} onChange={(e) => updateInvoiceField("invoiceNumber", e.target.value)} placeholder="CCG-ENQMTYTHD15-01" style={inputStyle} />
                      </Field>
                      <Field label="Amount (GBP)">
                        <input type="number" min="0" step="0.01" value={form.invoice.amount} onChange={(e) => updateInvoiceField("amount", e.target.value)} placeholder="180.00" style={inputStyle} />
                      </Field>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                      <Field label="Customer name">
                        <input value={form.invoice.customerName} onChange={(e) => updateInvoiceField("customerName", e.target.value)} placeholder="Customer or company name" style={inputStyle} />
                      </Field>
                      <Field label="Customer email">
                        <input value={form.invoice.customerEmail} onChange={(e) => updateInvoiceField("customerEmail", e.target.value)} placeholder="Uses To address if blank" style={inputStyle} />
                      </Field>
                    </div>

                    <Field label="Customer address">
                      <textarea value={form.invoice.customerAddress} onChange={(e) => updateInvoiceField("customerAddress", e.target.value)} placeholder={"Address line 1\nTown or city\nPostcode\nCountry"} style={{ ...inputStyle, minHeight: "100px", resize: "vertical" }} />
                    </Field>

                    <Field label="Service description">
                      <textarea value={form.invoice.description} onChange={(e) => updateInvoiceField("description", e.target.value)} placeholder="Journey and service details" style={{ ...inputStyle, minHeight: "90px", resize: "vertical" }} />
                    </Field>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px" }}>
                      <Field label="Issue date">
                        <input type="date" value={form.invoice.issueDate} onChange={(e) => updateInvoiceField("issueDate", e.target.value)} style={inputStyle} />
                      </Field>
                      <Field label="Due date (optional)">
                        <input type="date" value={form.invoice.dueDate} onChange={(e) => updateInvoiceField("dueDate", e.target.value)} style={inputStyle} />
                      </Field>
                      <Field label="Journey date (optional)">
                        <input type="date" value={form.invoice.journeyDate} onChange={(e) => updateInvoiceField("journeyDate", e.target.value)} style={inputStyle} />
                      </Field>
                    </div>

                    <Field label="Stripe payment link (optional)">
                      <input type="url" value={form.invoice.stripePaymentUrl} onChange={(e) => updateInvoiceField("stripePaymentUrl", e.target.value)} placeholder="Leave blank for bank transfer only" style={inputStyle} />
                    </Field>

                    <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "13px", lineHeight: 1.6, color: invoiceMeta?.bankConfigured && invoiceMeta?.registeredAddressConfigured ? "#166534" : "#991b1b" }}>
                        {invoiceMeta?.bankConfigured && invoiceMeta?.registeredAddressConfigured
                          ? `Ready: ${invoiceMeta.companyName}, company no. ${invoiceMeta.companyNumber}. No VAT charged.`
                          : "Server setup required: registered address and/or bank details are not configured."}
                      </div>
                      <button type="button" onClick={previewInvoice} disabled={previewingInvoice} style={{ ...secondaryButtonStyle, cursor: previewingInvoice ? "wait" : "pointer" }}>
                        {previewingInvoice ? "Generating..." : "Preview invoice PDF"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginTop: "22px", flexWrap: "wrap" }}>
                <div style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>
                  Invoice PDFs are generated on the server and attached only when selected. This screen is enabled outside production.
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  style={{
                    border: 0,
                    borderRadius: "14px",
                    padding: "14px 20px",
                    minWidth: "180px",
                    fontSize: "15px",
                    fontWeight: 800,
                    color: "#fff",
                    cursor: sending ? "wait" : "pointer",
                    background: profile.surface,
                    boxShadow: "0 18px 34px rgba(15, 23, 42, 0.18)",
                  }}
                >
                  {sending ? "Sending..." : "Send email"}
                </button>
              </div>

              {error ? (
                <div style={{ marginTop: "18px", borderRadius: "16px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "14px 16px", lineHeight: 1.6 }}>
                  {error}
                </div>
              ) : null}

              {success?.success ? (
                <div style={{ marginTop: "18px", borderRadius: "16px", background: "#ecfdf3", border: "1px solid #a7f3d0", color: "#065f46", padding: "14px 16px", lineHeight: 1.6 }}>
                  Email accepted. Message ID: {success?.result?.messageId || success?.result?.response || "sent"}
                  {form.attachInvoice ? ` · Attachments confirmed: ${success?.result?.attachmentCount ?? 0}` : ""}
                </div>
              ) : null}
            </form>

            <div style={{ display: "grid", gap: "18px" }}>
              <div style={{ background: "#ffffff", borderRadius: "28px", padding: "20px", boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)", border: "1px solid rgba(226, 232, 240, 0.9)" }}>
                <div style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "0.08em", color: "#64748b", textTransform: "uppercase", marginBottom: "12px" }}>Template preview</div>
                <div style={{ borderRadius: "24px", overflow: "hidden", border: "1px solid #dbe4f0", background: "#f8fafc" }}>
                  <div style={{ padding: "22px 24px", background: profile.surface, color: "#fff" }}>
                    <div style={{ fontSize: "13px", letterSpacing: "0.08em", opacity: 0.82, textTransform: "uppercase" }}>{profile.label}</div>
                    <div style={{ marginTop: "8px", fontSize: "28px", fontWeight: 800, lineHeight: 1.15 }}>{form.subject || "Subject line preview"}</div>
                  </div>
                  <div style={{ padding: "24px" }}>
                    <h2 style={{ margin: "0 0 16px", fontSize: "24px", color: "#0f172a" }}>{form.title || form.subject || "Email title preview"}</h2>
                    {form.recipientName ? <p style={previewTextStyle}>Hi {form.recipientName},</p> : null}
                    {previewParagraphs.length ? (
                      previewParagraphs.map((paragraph, index) => (
                        <p key={`${paragraph}-${index}`} style={previewTextStyle}>
                          {paragraph.split("\n").map((line, lineIndex) => (
                            <React.Fragment key={`${line}-${lineIndex}`}>
                              {line}
                              {lineIndex < paragraph.split("\n").length - 1 ? <br /> : null}
                            </React.Fragment>
                          ))}
                        </p>
                      ))
                    ) : (
                      <p style={previewTextStyle}>Your message preview will appear here.</p>
                    )}
                  </div>
                  <div style={{ padding: "18px 24px", background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                    <div style={{ color: "#475467", lineHeight: 1.7 }}>
                      {String(form.signatureBlock || profile.signatureBlock)
                        .split("\n")
                        .filter(Boolean)
                        .map((line, index) => {
                          const link = signatureLink(line);
                          return (
                            <div key={`${line}-${index}`} style={{ marginBottom: link ? "7px" : 0, fontWeight: index === 1 ? 800 : 400, color: index === 1 ? "#0f172a" : "#475467" }}>
                              {link ? (
                                <a href={link.href} style={{ color: profile.accent, textDecoration: "none", fontWeight: 700 }}>
                                  {link.label}
                                </a>
                              ) : line}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ background: "#ffffff", borderRadius: "28px", padding: "20px", boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)", border: "1px solid rgba(226, 232, 240, 0.9)" }}>
                <div style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "0.08em", color: "#64748b", textTransform: "uppercase", marginBottom: "12px" }}>Notes</div>
                <ul style={{ margin: 0, paddingLeft: "20px", color: "#475467", lineHeight: 1.8 }}>
                  <li>Use comma-separated addresses for `To`, `CC`, or `BCC` if needed.</li>
                  <li>Leave `Title` blank if the subject is enough.</li>
                  <li>Blank lines in the body become separate paragraphs in the final email.</li>
                  <li>The send route rejects requests in production.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "grid", gap: "8px" }}>
      <span style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#475467" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  padding: "14px 15px",
  fontSize: "15px",
  lineHeight: 1.5,
  color: "#0f172a",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
};

const secondaryButtonStyle = {
  border: "1px solid #93c5fd",
  borderRadius: "12px",
  padding: "11px 15px",
  background: "#fff",
  color: "#1d4ed8",
  fontSize: "14px",
  fontWeight: 800,
};

const previewTextStyle = {
  margin: "0 0 14px",
  color: "#243041",
  lineHeight: 1.75,
};
